import {apiRequest} from './api';
import {BaseRequestPayload} from './api/types/api-request.types';

// The API answers failures with {error: {code, message}}, which apiRequest turns
// into "Server error: {...}". This keeps only the message, so it can be shown as is.
export const readableMessage = (
  error: unknown,
  fallback: string,
): string => {
  const text = error instanceof Error ? error.message : '';
  const json = text.replace(/^Server error: /, '');
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed?.message === 'string' && parsed.message) {
      return parsed.message;
    }
  } catch {
    // Not JSON: an HTTP status or a network failure.
  }
  return text ? `${fallback} (${text})` : fallback;
};

// apiRequest swallows errors and returns null, which is also what a successful
// empty response looks like, so failures are tracked through onError.
export const adminRequest = async <TResponse, TBody = undefined>(
  payload: BaseRequestPayload<TBody>,
  failureMessage: string,
): Promise<TResponse | null> => {
  let failure: Error | null = null;

  const response = await apiRequest<TResponse, TBody>(payload, {
    errorMessage: failureMessage,
    onError: (error: unknown) => {
      failure = new Error(readableMessage(error, failureMessage));
    },
  });

  if (failure) {
    throw failure;
  }

  return response?.data ?? null;
};

export const requireData = <T>(data: T | null, message: string): T => {
  if (data === null) {
    throw new Error(message);
  }
  return data;
};
